/**
 * Bridge between the track mutations and the globally mounted
 * `TrackRequirementHost` dialog — the track twin of `transitionNoteGate`.
 *
 * When the server refuses a track status change because the lead does not
 * meet a track requirement (400 + `data.trackRequirement`, see
 * core/tracks/requirements/trackRequirement.gate.ts), the track mutation
 * calls `requestTrackRequirementFix` to open the popup that lets the person
 * add what is missing right there, then replays the same change when the
 * popup says "continue". Cancelling (or no mounted host) resolves false and
 * the caller surfaces the original error as usual.
 *
 * Kept in `lib` (dependency-free) so the mutation hooks never import
 * feature components.
 */

export interface TrackRequirementFailure {
  type: string;
  referenceKey: string;
  errorMessage: string;
  requirementId?: number;
  /** Custom field / role / user / module / track id, or the DealCount minimum. */
  referenceId?: number | null;
}

export interface TrackRequirementPrompt {
  /** The gate's own first message. */
  message: string;
  failures: TrackRequirementFailure[];
  trackId: number;
  /** The track status the change was trying to reach. */
  subTrackId: number;
  entityType: "lead" | "child";
  leadId: number;
  childId: number | null;
}

/** Resolves true when the person wants the change replayed, false on cancel. */
type Handler = (prompt: TrackRequirementPrompt) => Promise<boolean>;

let handler: Handler | null = null;
/** Serialises concurrent prompts so two refusals never race one dialog. */
let queue: Promise<unknown> = Promise.resolve();

export function registerTrackRequirementPrompt(h: Handler | null): void {
  handler = h;
}

export function requestTrackRequirementFix(
  prompt: TrackRequirementPrompt,
): Promise<boolean> {
  if (!handler) return Promise.resolve(false);
  const run = queue.then(() => (handler ? handler(prompt) : false));
  queue = run.catch(() => false);
  return run;
}

/**
 * The refusal the gate sends, read off an axios error. Null for any other
 * error, so callers fall through to their normal handling.
 */
export function readTrackRequirementRefusal(error: any): {
  message: string;
  failures: TrackRequirementFailure[];
  trackId: number;
  subTrackId: number;
  entityType?: "lead" | "child";
  leadId?: number;
  childId?: number | null;
} | null {
  if (error?.response?.status !== 400) return null;
  const body = error.response?.data;
  const gate = body?.data?.trackRequirement;
  const failures = body?.data?.requirements;
  if (!gate?.trackId || !gate?.subTrackId) return null;
  if (!Array.isArray(failures) || failures.length === 0) return null;
  return {
    message: String(body?.message || "This track status needs a few things first."),
    failures,
    trackId: Number(gate.trackId),
    subTrackId: Number(gate.subTrackId),
    entityType: gate.entityType === "child" ? "child" : gate.entityType === "lead" ? "lead" : undefined,
    leadId: gate.leadId != null ? Number(gate.leadId) : undefined,
    childId: gate.childId != null ? Number(gate.childId) : null,
  };
}
